import type { Permission } from '@nw/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/** GET /api/auth/me — implemented by lane B1. Shape is the contract. */
export interface Me {
  id: string;
  email: string;
  name: string;
  roleName: string;
  permissions: Permission[];
  mfaEnabled: boolean;
  mfaVerified: boolean;
}

export const meQueryKey = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({ queryKey: meQueryKey, queryFn: () => api<{ user: Me }>('/api/auth/me').then((r) => r.user), staleTime: 60_000 });
}

export function useCan(): (p: Permission) => boolean {
  const { data } = useMe();
  return (p) => Boolean(data?.permissions.includes(p));
}

/** GET /api/statusbar — implemented by lane B9 (reads channel/outbox/incidents). Shape is the contract. */
export interface StatusBar {
  /** D-150: the worst of the monitored WhatsApp numbers (lowest status rank, then the oldest) */
  channel: { id: string; name: string; status: string; phone: string | null; statusChangedAt: string } | null;
  /** D-150: every WhatsApp number of the clients, each with its own quota (optional: a server from before D-150 omits it) */
  channels?: { id: string; name: string; status: string; phone: string | null; statusChangedAt: string; quota: { used: number; cap: number } }[];
  /** the shown channel's quota */
  quota: { used: number; cap: number };
  queue: { queued: number; held: number };
  killSwitch: { global: boolean };
  incidents: { open: number; critical: number; latest?: { id: string; title: string; severity: string; openedAt: string } };
  counts: { chatsUnread: number; groupsUnassigned: number; eventsErrors: number };
}

export function useStatusBar() {
  return useQuery({ queryKey: ['statusbar'], queryFn: () => api<StatusBar>('/api/statusbar'), refetchInterval: 60_000 });
}
